import type { RecoveryIdentity, RemoteRecoveryReceipt } from '../../types/recovery';

export interface RecoveryCanonicalPullContext {
  identity: RecoveryIdentity;
  recoveryId: string;
  manifestSha512: string;
  receipt: RemoteRecoveryReceipt;
}

export interface RecoveryCanonicalPullOptions {
  forceFullSync: true;
  skipPush: true;
  managedBySyncAll: false;
  tracePrefix: string;
}

export interface RecoveryCanonicalPullStatus {
  success: boolean;
  message?: string;
  timestamp: Date;
  details?: Record<string, any>;
}

export interface RecoveryCanonicalTableNames {
  templates: string;
  templateVersions: string;
  checklists: string;
  snapshots: string;
  snapshotFields: string;
}

// O chamador deve validar criptograficamente o contexto antes de usar esta identidade.
export const resolveRecoveryPullCompanyId = (input: {
  authenticatedUserId: string;
  localCompanyId: string | null;
  context: RecoveryCanonicalPullContext | null;
}): string | null => {
  if (!input.context) return input.localCompanyId;
  if (input.context.identity.userId !== input.authenticatedUserId) {
    throw new Error('Identidade autenticada diverge do receipt de recovery.');
  }
  return input.context.identity.companyId;
};

export const includeRecoveryHistoricalTables = (
  baseTables: readonly string[],
  names: RecoveryCanonicalTableNames
): string[] => {
  const tables = [...baseTables];
  const insertAfter = (anchor: string, additions: string[]) => {
    const index = tables.indexOf(anchor);
    if (index < 0) throw new Error(`Tabela base ausente no pull de recovery: ${anchor}.`);
    tables.splice(index + 1, 0, ...additions.filter(table => !tables.includes(table)));
  };
  insertAfter(names.templates, [names.templateVersions]);
  insertAfter(names.checklists, [names.snapshots, names.snapshotFields]);
  return tables;
};

export const includeHistoricalTablesWhenEnabled = (
  baseTables: readonly string[],
  names: RecoveryCanonicalTableNames,
  enabled: boolean
): string[] => enabled
  ? includeRecoveryHistoricalTables(baseTables, names)
  : [...baseTables];

export class RecoveryCanonicalPullService {
  constructor(
    private readonly isVerified: (context: RecoveryCanonicalPullContext) => boolean,
    private readonly pull: (
      options: RecoveryCanonicalPullOptions,
      context: RecoveryCanonicalPullContext
    ) => Promise<RecoveryCanonicalPullStatus>
  ) {}

  execute(context: RecoveryCanonicalPullContext): Promise<RecoveryCanonicalPullStatus> {
    if (!this.isVerified(context)) {
      return Promise.resolve({
        success: false,
        message: 'Pull de recuperacao nao autorizado.',
        timestamp: new Date(),
        details: { reason: 'recovery_receipt_not_verified' },
      });
    }

    return this.pull({
      forceFullSync: true,
      skipPush: true,
      managedBySyncAll: false,
      tracePrefix: `[recovery:${context.recoveryId}]`,
    }, context);
  }
}
